"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

type FadeInProps = {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    duration?: number;
    y?: number;
    threshold?: number;
    once?: boolean;
};

export function FadeIn({
    children,
    className,
    delay = 0,
    duration = 0.5,
    y = 20,
    threshold = 0.2,
    once = true
}: FadeInProps) {
    const ref = useRef(null);
    const isInView = useInView(ref, { amount: threshold, once });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
            transition={{
                duration,
                delay,
                ease: [0.21, 0.47, 0.32, 0.98]
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function Stagger({
    children,
    className,
    delay = 0,
    staggerDelay = 0.1,
    threshold = 0.2,
    once = true
}: {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    staggerDelay?: number;
    threshold?: number;
    once?: boolean;
}) {
    const ref = useRef(null);
    const isInView = useInView(ref, { amount: threshold, once });

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={isInView ? "show" : "hidden"}
            variants={{
                hidden: {},
                show: {
                    transition: {
                        staggerChildren: staggerDelay,
                        delayChildren: delay
                    }
                }
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function FadeInStaggerItem({ children, className }: { children: React.ReactNode, className?: string }) {
    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}
